import axios from 'axios';
import { API_ENDPOINTS, API_BASE_URL, debugApiCall } from './apiRegistry';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '../utils/safeStorage';
import { getAdminTenantId } from '../utils/tenantHelper';

const ADMIN_API_BASE_URL = API_BASE_URL;

// Debug logging
console.log('Admin API Base URL:', API_BASE_URL);
console.log('Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and tenant header to requests and log them
adminApi.interceptors.request.use((config) => {
  const token = getLocalStorageItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add admin tenant header (separate from team tenant so admin panel doesn't overwrite team context)
  const tenantId = getAdminTenantId();
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  console.log('[Tenant] X-Tenant-ID sent with request', { url: config.url, tenantId: tenantId || '(none)' });
  
  console.log('🚀 Admin API Request:', {
    url: config.url,
    method: config.method,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
    headers: config.headers,
    data: config.data
  });
  
  return config;
});

/** Navigation, strict-mode remount, query key change, or React Query replacing an in-flight request. */
export function isBenignClientAbort(error) {
  if (!error) return false;
  const msg = String(error.message || '');
  // Axios uses ECONNABORTED for both user-visible timeouts and some aborts; do not treat timeouts as benign.
  if (/timeout/i.test(msg)) return false;
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
    return true;
  }
  if (error.code === 'ECONNABORTED' && /timeout of/i.test(msg)) return false;
  if (error.code === 'ECONNABORTED') return true;
  if (msg === 'Request aborted' || /aborted|cancel(c)?ed|cancelled/i.test(msg)) return true;
  return false;
}

// Handle token expiration and add better error logging
adminApi.interceptors.response.use(
  (response) => {
    console.log('✅ Admin API Response:', {
      url: response.config?.url,
      method: response.config?.method,
      status: response.status,
      data: response.data
    });
    return response.data; // Return just the data, not the full response
  },
  (error) => {
    if (isBenignClientAbort(error)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Admin request skipped (aborted):', error.config?.url);
      }
      return Promise.reject(error);
    }

    console.error('❌ Admin API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      requestData: error.config?.data
    });
    
    // Log the full error for debugging
    if (error.response?.data) {
      console.error('❌ Error response data:', JSON.stringify(error.response.data, null, 2));
    }
    
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      if (reqUrl.includes('/admin/auth/login')) {
        return Promise.reject(error);
      }
      console.log('🔐 Admin token expired, clearing admin auth');
      removeLocalStorageItem('adminToken');
      removeLocalStorageItem('adminUser');
      // Only redirect if not already on login page
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin/login') {
        console.log('🔄 Redirecting to admin login');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const adminAuthAPI = {
  login: (credentials) => {
    debugApiCall(API_ENDPOINTS.ADMIN_AUTH.LOGIN, credentials);
    return adminApi.post(API_ENDPOINTS.ADMIN_AUTH.LOGIN, credentials);
  },
  logout: () => {
    debugApiCall(API_ENDPOINTS.ADMIN_AUTH.LOGOUT);
    return adminApi.post(API_ENDPOINTS.ADMIN_AUTH.LOGOUT);
  },
  getProfile: () => {
    debugApiCall(API_ENDPOINTS.ADMIN_AUTH.PROFILE);
    return adminApi.get(API_ENDPOINTS.ADMIN_AUTH.PROFILE);
  },
  updateProfile: (data) => {
    debugApiCall(API_ENDPOINTS.ADMIN_AUTH.PROFILE, data);
    return adminApi.patch(API_ENDPOINTS.ADMIN_AUTH.PROFILE, data);
  },
};

export const adminTeamsAPI = {
  getAll: (params = {}) => {
    debugApiCall(API_ENDPOINTS.ADMIN_TEAMS.LIST, params);
    return adminApi.get(API_ENDPOINTS.ADMIN_TEAMS.LIST, { params });
  },
  getById: (id) => {
    debugApiCall(API_ENDPOINTS.ADMIN_TEAMS.GET(id));
    return adminApi.get(API_ENDPOINTS.ADMIN_TEAMS.GET(id));
  },
  /** @param {string} id @param {{ seasonId?: string }} [opts] – when set, division matches list view (TDA for that season) */
  getTeamDetails: (id, opts = {}) => {
    const { seasonId } = opts;
    const params = seasonId ? { season_id: seasonId } : undefined;
    debugApiCall(`/admin/teams/${id}/details`, params || {});
    return adminApi.get(`/admin/teams/${id}/details`, { params });
  },
  update: (id, data) => {
    debugApiCall(API_ENDPOINTS.ADMIN_TEAMS.UPDATE(id), data);
    return adminApi.put(API_ENDPOINTS.ADMIN_TEAMS.UPDATE(id), data);
  },
  delete: (id) => {
    debugApiCall(API_ENDPOINTS.TEAMS.DELETE(id));
    return adminApi.delete(API_ENDPOINTS.TEAMS.DELETE(id));
  },
  assignDivision: (teamId, divisionId) => {
    debugApiCall(`/admin/teams/${teamId}/assign-division`, { divisionId });
    return adminApi.post(`/admin/teams/${teamId}/assign-division`, { divisionId });
  },
  uploadLogo: (teamId, file) => {
    const formData = new FormData();
    formData.append('teamLogo', file);
    return adminApi.post(`/admin/teams/${teamId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeLogo: (teamId) => {
    return adminApi.delete(`/admin/teams/${teamId}/logo`);
  },
  updateMemberRegistrationPayment: (teamId, playerId, { status, seasonId }) =>
    adminApi.patch(`/admin/teams/${teamId}/members/${playerId}/registration-payment`, {
      status,
      seasonId,
    }),
};

/** Broadcast email (Settings → Email): season, recipients, subject, HTML body */
export const adminCommunicationsAPI = {
  teamSearch: (params) => {
    debugApiCall('/admin/communications/team-search', params);
    return adminApi.get('/admin/communications/team-search', { params });
  },
  broadcastEmail: (payload) => {
    debugApiCall('/admin/communications/broadcast-email', payload);
    return adminApi.post('/admin/communications/broadcast-email', payload, { timeout: 0 });
  },
  broadcastPreview: (payload) => {
    debugApiCall('/admin/communications/broadcast-preview', payload);
    return adminApi.post('/admin/communications/broadcast-preview', payload);
  },
  getBroadcastJob: (jobId) => {
    debugApiCall(`/admin/communications/broadcast-jobs/${jobId}`);
    return adminApi.get(`/admin/communications/broadcast-jobs/${jobId}`);
  },
};

export const adminBulkEmailAPI = {
  downloadTemplate: () =>
    adminApi.get('/admin/bulk-email/template', { responseType: 'blob' }),
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return adminApi.post('/admin/bulk-email/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearSession: (sessionId) => adminApi.delete(`/admin/bulk-email/sessions/${sessionId}`),
  send: (payload) =>
    adminApi.post('/admin/bulk-email/send', payload, { timeout: 0 }),
  getJob: (jobId) => adminApi.get(`/admin/bulk-email/jobs/${jobId}`),
};

export const adminEmailAttachmentsAPI = {
  upload: (file, sessionId, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    if (sessionId) form.append('sessionId', sessionId);
    return adminApi.post('/admin/email-attachments/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  removeFile: (sessionId, fileId) =>
    adminApi.delete(`/admin/email-attachments/${sessionId}/files/${fileId}`),
  clearSession: (sessionId) =>
    adminApi.delete(`/admin/email-attachments/${sessionId}`),
  getLimits: () => adminApi.get('/admin/email-attachments/limits'),
};

/** In-app player home banners (Settings → Communications). */
export const adminPlayerHomeBannersAPI = {
  list: () => {
    debugApiCall('/admin/communications/player-home-banners');
    return adminApi.get('/admin/communications/player-home-banners');
  },
  create: (payload) => {
    debugApiCall('/admin/communications/player-home-banners', payload);
    return adminApi.post('/admin/communications/player-home-banners', payload);
  },
  update: (id, payload) => {
    debugApiCall(`/admin/communications/player-home-banners/${id}`, payload);
    return adminApi.patch(`/admin/communications/player-home-banners/${id}`, payload);
  },
  delete: (id) => {
    debugApiCall(`/admin/communications/player-home-banners/${id}`);
    return adminApi.delete(`/admin/communications/player-home-banners/${id}`);
  },
};

export const adminPlayersAPI = {
  getAll: (params = {}) => adminApi.get('/admin/players', { params }),
  getList: (params = {}) => adminApi.get('/admin/players', { params }),
  getById: (id) => adminApi.get(`/admin/players/${id}`),
  getStats: (id, params = {}) => adminApi.get(`/admin/players/${id}/stats`, { params }),
  updateSkillLevel: (id, skillLevel) =>
    adminApi.patch(`/admin/players/${id}/skill-level`, { skillLevel }),
  update: (id, data) => adminApi.put(`/admin/players/${id}`, data),
  sendPasswordReset: (id, data = {}) =>
    adminApi.post(`/admin/players/${id}/send-password-reset`, data),
  impersonate: (id) => adminApi.post(`/admin/players/${id}/impersonate`, {}),
  delete: (id) => adminApi.delete(`/admin/players/${id}`),
};

export const adminDivisionsAPI = {
  getAll: () => adminApi.get('/admin/divisions'),
  create: (data) => adminApi.post('/admin/divisions', data),
  update: (id, data) => adminApi.put(`/admin/divisions/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/divisions/${id}`),
};

export const adminMatchesAPI = {
  getAll: (params = {}) => adminApi.get('/admin/matches', { params }),
  getScheduled: (params = {}) => adminApi.get('/admin/scheduled-matches', { params }),
  create: (data) => adminApi.post('/admin/matches', data),
  update: (id, data) => adminApi.put(`/admin/matches/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/matches/${id}`),
  putScore: (matchId, scores) => adminApi.put(`/admin/matches/${matchId}/score`, { scores }),
  deleteScore: (matchId) => adminApi.delete(`/admin/matches/${matchId}/score`),
  confirmScore: (matchId) => adminApi.post(`/admin/matches/${matchId}/confirm-score`),
};

export const adminAnalyticsAPI = {
  /** CSV: 1st & 2nd per division for season (blob). On API error, may be JSON blob. */
  downloadDivisionTopTwoCsv: (seasonId) =>
    adminApi.get('/admin/analytics/division-top-two.csv', {
      params: { season_id: seasonId },
      responseType: 'blob'
    }),
};

/** Campaign-to-campaign top-two carry promotion (separate from season Team Placement). */
export const adminCampaignCarryPromotionAPI = {
  getSourceCampaigns: () => adminApi.get('/admin/campaign-carry-promotion/source-campaigns'),
  getTargetCampaigns: () => adminApi.get('/admin/campaign-carry-promotion/target-campaigns'),
  getCampaignSeasons: (campaignId) =>
    adminApi.get(`/admin/campaign-carry-promotion/campaigns/${campaignId}/seasons`),
  preview: (params) => adminApi.get('/admin/campaign-carry-promotion/preview', { params }),
  apply: (payload) => adminApi.post('/admin/campaign-carry-promotion/apply', payload),
};

export const adminSkillLevelAlignmentAPI = {
  getFinalSprintAudit: (params) => adminApi.get('/admin/skill-level-alignment/final-sprint-audit', { params }),
  getOpeningAudit: (params) => adminApi.get('/admin/skill-level-alignment/opening-audit', { params }),
  getFinalSprintPreview: (params) => adminApi.get('/admin/skill-level-alignment/final-sprint-preview', { params }),
  applyFinalSprint: (payload) => adminApi.post('/admin/skill-level-alignment/final-sprint-apply', payload),
  repairOpening: (payload) => adminApi.post('/admin/skill-level-alignment/opening-repair', payload),
};

export const adminSeasonsAPI = {
  getAll: () => adminApi.get('/admin/seasons'),
  create: (data) => adminApi.post('/admin/seasons', data),
  update: (id, data) => adminApi.put(`/admin/seasons/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/seasons/${id}`),
  activate: (id) => adminApi.post(`/admin/seasons/${id}/activate`),
  /** Copy team registrations from source season into target (backfill when activation used wrong previous season). */
  registerTeamsFromSeason: (targetSeasonId, sourceSeasonId) =>
    adminApi.post(`/admin/seasons/${targetSeasonId}/register-teams-from-season`, {
      source_season_id: sourceSeasonId
    }),
};

export const adminEventTournamentsAPI = {
  getAll: () => adminApi.get('/admin/event-tournaments'),
  getTenantSeasons: () => adminApi.get('/admin/event-tournaments/seasons'),
  getLinkedSeasons: (id) => adminApi.get(`/admin/event-tournaments/${id}/seasons`),
  linkSeasons: (id, seasonIds) => adminApi.post(`/admin/event-tournaments/${id}/seasons`, { season_ids: seasonIds }),
  createSeasonInCampaign: (id, data) => adminApi.post(`/admin/event-tournaments/${id}/seasons/create`, data),
  updateLinkedSeason: (id, seasonId, data) => adminApi.patch(`/admin/event-tournaments/${id}/seasons/${seasonId}`, data),
  copyDivisionsBetweenSeasons: (id, payload) =>
    adminApi.post(`/admin/event-tournaments/${id}/seasons/copy-divisions`, payload),
  unlinkSeason: (id, seasonId) => adminApi.delete(`/admin/event-tournaments/${id}/seasons/${seasonId}`),
  create: (form) => adminApi.post('/admin/event-tournaments', form),
  update: (id, form) => adminApi.patch(`/admin/event-tournaments/${id}`, form),
  publish: (id) => adminApi.post(`/admin/event-tournaments/${id}/publish`),
  republish: (id) => adminApi.post(`/admin/event-tournaments/${id}/republish`),
  discontinue: (id) => adminApi.post(`/admin/event-tournaments/${id}/discontinue`),
  delete: (id) => adminApi.delete(`/admin/event-tournaments/${id}`),
  uploadBanner: (id, file) => {
    const fd = new FormData();
    fd.append('banner', file);
    return adminApi.post(`/admin/event-tournaments/${id}/banner`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const adminTenantSettingsAPI = {
  get: () => adminApi.get('/admin/settings/tenant'),
  update: (data) => adminApi.patch('/admin/settings/tenant', data),
  getRegistrationPage: () => adminApi.get('/admin/settings/registration-page'),
  updateRegistrationPage: (data) => adminApi.patch('/admin/settings/registration-page', data),
  uploadTournamentLogo: (file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return adminApi.post('/admin/settings/tenant/tournament-logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

/** Venue club catalog for match details dropdown */
export const adminClubsAPI = {
  list: (opts = {}) =>
    adminApi
      .get('/admin/clubs', {
        params: {
          ...(opts.includeInactive ? { include_inactive: '1' } : {}),
          ...(opts.partnersOnly ? { partners_only: '1' } : {}),
        },
      })
      .then((res) => res.data),
  create: (data) => adminApi.post('/admin/clubs', data).then((res) => res.data),
  update: (id, data) => adminApi.patch(`/admin/clubs/${id}`, data).then((res) => res.data),
  remove: (id) => adminApi.delete(`/admin/clubs/${id}`).then((res) => res.data),
};

/** Admin-organised bookable events (tournaments / camps) — not league campaigns */
export const adminOrganisedEventsAPI = {
  list: (params = {}) =>
    adminApi.get('/admin/organised-events', { params }).then((res) => res.data),
  get: (id) => adminApi.get(`/admin/organised-events/${id}`).then((res) => res.data),
  create: (data) => adminApi.post('/admin/organised-events', data).then((res) => res.data),
  update: (id, data) => adminApi.patch(`/admin/organised-events/${id}`, data).then((res) => res.data),
  publish: (id) => adminApi.post(`/admin/organised-events/${id}/publish`).then((res) => res.data),
  close: (id) => adminApi.post(`/admin/organised-events/${id}/close`).then((res) => res.data),
  duplicate: (id) => adminApi.post(`/admin/organised-events/${id}/duplicate`).then((res) => res.data),
  cancel: (id) => adminApi.post(`/admin/organised-events/${id}/cancel`).then((res) => res.data),
  uploadCover: (id, file) => {
    const fd = new FormData();
    fd.append('cover', file);
    return adminApi
      .post(`/admin/organised-events/${id}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },
};

/** Super Admin only: list, create, update tenants */
export const adminTenantsAPI = {
  getList: () => adminApi.get('/admin/tenants'),
  create: (data) => adminApi.post('/admin/tenants', data),
  update: (id, data) => adminApi.patch(`/admin/tenants/${id}`, data),
};

/** Stripe Connect (Express): tenant admin uses from Settings (no tenant_id); Super Admin can pass tenant_id */
export const adminConnectAPI = {
  createAccountLink: (payload = {}) => adminApi.post('/admin/connect/create-account-link', payload),
  getLoginLink: (tenantId) => adminApi.get('/admin/connect/login-link', { params: tenantId ? { tenant_id: tenantId } : {} }),
  disconnect: () => adminApi.post('/admin/connect/disconnect'),
};

/** Super Admin only: list, create, update, remove admin users (reassign tenant/role) */
export const adminUsersAPI = {
  getList: () => adminApi.get('/admin/admin-users'),
  create: (data) => adminApi.post('/admin/admin-users', data),
  update: (id, data) => adminApi.patch(`/admin/admin-users/${id}`, data),
  remove: (id) => adminApi.delete(`/admin/admin-users/${id}`),
};

/** Super Admin: Phase 2 membership audit/report */
export const adminPhase2AuditAPI = {
  getMembershipAudit: () => adminApi.get('/admin/phase2/membership-audit'),
  exportMembershipAudit: () => adminApi.get('/admin/phase2/membership-audit/export', { responseType: 'blob' }),
};

/** Super Admin only: toggle player in-app help assistant */
export const adminPlayerHelpAssistantAPI = {
  getSetting: () => adminApi.get('/admin/player-help-assistant/setting'),
  putSetting: (body) => adminApi.put('/admin/player-help-assistant/setting', body),
};

/** Super Admin only: toggle player in-app help wizard */
export const adminPlayerHelpWizardAPI = {
  getSetting: () => adminApi.get('/admin/player-help-wizard/setting'),
  putSetting: (body) => adminApi.put('/admin/player-help-wizard/setting', body),
};

/** Resolve tenant by slug (for dashboard URL sync so X-Tenant-ID matches current URL) */
export const adminTenantBySlugAPI = {
  get: (slug) => adminApi.get(`/admin/tenant-by-slug/${encodeURIComponent(slug)}`),
};

/** Tenant-scoped: cantons (for registration/division dropdowns) - country or court tenant */
export const adminCantonsAPI = {
  getList: () => adminApi.get('/admin/cantons'),
};

/** Admin provisioning: player account + shell team + roster links (outside checkout). */
export const adminTeamProvisioningAPI = {
  searchTeams: (params = {}) => adminApi.get('/admin/provision/teams-search', { params }),
  searchPlayers: (params = {}) => adminApi.get('/admin/provision/players-search', { params }),
  createPlayer: (body) => adminApi.post('/admin/provision/players', body),
  createTeamShell: (body) => adminApi.post('/admin/provision/teams-shell', body),
  linkPlayerToTeam: (body) => adminApi.post('/admin/provision/link-player-to-team', body),
  playerThenTeamCaptain: (body) => adminApi.post('/admin/provision/player-then-team-captain', body),
  createSeasonRoster: (body) => adminApi.post('/admin/provision/season-roster', body),
};

/** Tenant-scoped: roles (list, create, update, delete) - requires X-Tenant-ID */
export const adminRolesAPI = {
  getList: () => adminApi.get('/admin/roles'),
  create: (data) => adminApi.post('/admin/roles', data),
  update: (id, data) => adminApi.patch(`/admin/roles/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/roles/${id}`),
};

/** Tenant-scoped: users (list, create, update) - requires X-Tenant-ID */
export const adminTenantUsersAPI = {
  getList: () => adminApi.get('/admin/users'),
  create: (data) => adminApi.post('/admin/users', data),
  update: (id, data) => adminApi.patch(`/admin/users/${id}`, data),
};

export const adminPromoCodesAPI = {
  getAll: () => adminApi.get('/admin/promo-codes'),
  create: (data) => adminApi.post('/admin/promo-codes', data),
  update: (id, data) => adminApi.patch(`/admin/promo-codes/${id}`, data),
};

export const adminRegistrationProductsAPI = {
  getAll: () => adminApi.get('/admin/registration-products'),
  create: (data) => adminApi.post('/admin/registration-products', data),
  update: (id, data) => adminApi.patch(`/admin/registration-products/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/registration-products/${id}`),
  uploadImage: (id, formData) =>
    adminApi.post(`/admin/registration-products/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getDeliverySettings: () => adminApi.get('/admin/registration-products/meta/delivery-settings'),
  updateDeliverySettings: (data) =>
    adminApi.patch('/admin/registration-products/meta/delivery-settings', data),
};

export const adminShopAPI = {
  getShipping: () => adminApi.get('/admin/shop/shipping'),
  updateShipping: (data) => adminApi.patch('/admin/shop/shipping', data),
  getCategories: () => adminApi.get('/admin/shop/categories'),
  createCategory: (data) => adminApi.post('/admin/shop/categories', data),
  updateCategory: (id, data) => adminApi.patch(`/admin/shop/categories/${id}`, data),
  deleteCategory: (id) => adminApi.delete(`/admin/shop/categories/${id}`),
  getProducts: () => adminApi.get('/admin/shop/products'),
  createProduct: (data) => adminApi.post('/admin/shop/products', data),
  updateProduct: (id, data) => adminApi.patch(`/admin/shop/products/${id}`, data),
  deleteProduct: (id) => adminApi.delete(`/admin/shop/products/${id}`),
  bulkProducts: (data) => adminApi.post('/admin/shop/products/bulk', data),
  uploadImage: (id, formData) =>
    adminApi.post(`/admin/shop/products/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  setPrimaryImage: (id, imageId) =>
    adminApi.patch(`/admin/shop/products/${id}/images/${imageId}/primary`),
  reorderImages: (id, imageIds) =>
    adminApi.put(`/admin/shop/products/${id}/images/order`, { imageIds }),
  deleteImage: (id, imageId) => adminApi.delete(`/admin/shop/products/${id}/images/${imageId}`),
  getVariants: (productId) => adminApi.get(`/admin/shop/products/${productId}/variants`),
  replaceVariants: (productId, data) =>
    adminApi.put(`/admin/shop/products/${productId}/variants`, data),
};

export const adminRegistrationOrdersAPI = {
  getAll: (params) => adminApi.get('/admin/registration-orders', { params }),
  get: (id) => adminApi.get(`/admin/registration-orders/${id}`),
  update: (id, data) => adminApi.patch(`/admin/registration-orders/${id}`, data),
  downloadCsv: () =>
    adminApi.get('/admin/registration-orders/export', { responseType: 'blob' }),
};

export const adminStatsAPI = {
  getOverview: () => {
    debugApiCall(API_ENDPOINTS.ADMIN_TEAMS.STATS_OVERVIEW);
    return adminApi.get(API_ENDPOINTS.ADMIN_TEAMS.STATS_OVERVIEW);
  },
  getRegistrationSources: (seasonId) => {
    const params = seasonId ? { season_id: seasonId } : {};
    debugApiCall('/admin/stats/registration-sources', params);
    return adminApi.get('/admin/stats/registration-sources', { params });
  },
  getRegistrationPayments: ({ seasonId, filter = 'all' } = {}) => {
    const params = { season_id: seasonId, filter };
    debugApiCall('/admin/stats/registration-payments', params);
    return adminApi.get('/admin/stats/registration-payments', { params });
  },
  downloadRegistrationPaymentsCsv: ({ seasonId, filter = 'all' } = {}) =>
    adminApi.get('/admin/stats/registration-payments.csv', {
      params: { season_id: seasonId, filter },
      responseType: 'blob',
    }),
  getLeagueAnalytics: (params = {}) => {
    const q = {};
    if (params.seasonId) q.season_id = params.seasonId;
    if (params.campaignId) q.campaign_id = params.campaignId;
    if (params.canton) q.canton = params.canton;
    debugApiCall('/admin/stats/league-analytics', q);
    return adminApi.get('/admin/stats/league-analytics', { params: q });
  },
  getPartnerClubReport: (params = {}) => {
    const q = {};
    if (params.clubId) q.club_id = params.clubId;
    if (params.from) q.from = params.from;
    if (params.to) q.to = params.to;
    if (params.seasonId) q.season_id = params.seasonId;
    debugApiCall('/admin/stats/partner-club-report', q);
    return adminApi.get('/admin/stats/partner-club-report', { params: q });
  },
  getTeams: () => {
    debugApiCall('/admin/stats/teams');
    return adminApi.get('/admin/stats/teams');
  },
  getPlayers: () => {
    debugApiCall('/admin/stats/players');
    return adminApi.get('/admin/stats/players');
  },
  getMatches: () => {
    debugApiCall('/admin/stats/matches');
    return adminApi.get('/admin/stats/matches');
  },
};

export const adminPartnerCommunicationsAPI = {
  getAll: (params = {}) => {
    debugApiCall('/admin/partner-communications', params);
    return adminApi.get('/admin/partner-communications', { params });
  },
  export: (params = {}) => {
    debugApiCall('/admin/partner-communications/export', params);
    return adminApi.get('/admin/partner-communications/export', { params });
  },
};

/** Season bulk emails (league management): teams with few completed matches, etc. */
export const adminSeasonCommunicationsAPI = {
  getLowMatchCountTeams: (seasonId, params = {}) =>
    adminApi.get(`/admin/seasons/${seasonId}/low-match-count-teams`, { params }),
  sendLowMatchReminder: (seasonId, body) =>
    adminApi.post(`/admin/seasons/${seasonId}/send-low-match-reminder`, body),
};

/** Mid-season: vacant-slot replacement (remap fixtures in one division/season to a new team). */
export const adminTeamSlotSwapAPI = {
  outgoingCandidates: (seasonId) =>
    adminApi.get(`/admin/seasons/${seasonId}/team-slot-swap/outgoing-candidates`),
  incomingCandidates: (seasonId, params = {}) =>
    adminApi.get(`/admin/seasons/${seasonId}/team-slot-swap/incoming-candidates`, { params }),
  listAudits: (params = {}) => adminApi.get('/admin/team-slot-swap/audits', { params }),
  preview: (seasonId, body) => adminApi.post(`/admin/seasons/${seasonId}/team-slot-swap/preview`, body),
  execute: (seasonId, body) => adminApi.post(`/admin/seasons/${seasonId}/team-slot-swap`, body),
};

export const adminLeagueManagementAPI = {
  getDivisionsOverview: (seasonId) => {
    debugApiCall(`/league-management/admin/divisions-overview?season_id=${seasonId}`);
    return adminApi.get(`/league-management/admin/divisions-overview?season_id=${seasonId}`);
  },
  getDivisionsByLevel: (seasonId) => {
    debugApiCall(`/league-management/admin/divisions-by-level?season_id=${seasonId}`);
    return adminApi.get(`/league-management/admin/divisions-by-level?season_id=${seasonId}`);
  },
  getDivisionTeams: (divisionId) => {
    debugApiCall(`/league-management/admin/divisions/${divisionId}/teams`);
    return adminApi.get(`/league-management/admin/divisions/${divisionId}/teams`);
  },
  moveTeam: (teamId, fromDivisionId, toDivisionId, seasonId, notes) => {
    debugApiCall('/league-management/admin/move-team', { teamId, fromDivisionId, toDivisionId, seasonId, notes });
    // No client timeout: move-team waits on one DB transaction; under load (pool, cold start, or
    // audit triggers) a long run can happen. A short axios timeout only caused false errors.
    return adminApi.post('/league-management/admin/move-team', {
      teamId,
      fromDivisionId,
      toDivisionId,
      seasonId,
      notes,
    });
  },
  createDivision: (seasonId, canton, divisionLevel, groupName, maxTeams) => {
    debugApiCall('/league-management/admin/divisions', { seasonId, canton, divisionLevel, groupName, maxTeams });
    return adminApi.post('/league-management/admin/divisions', { seasonId, canton, divisionLevel, groupName, maxTeams });
  },
  generateMatches: (divisionId, seasonId, leagueStartDate) => {
    debugApiCall(`/league-management/admin/divisions/${divisionId}/generate-matches`, { seasonId, leagueStartDate });
    return adminApi.post(`/league-management/admin/divisions/${divisionId}/generate-matches`, { seasonId, leagueStartDate });
  },
  getDivisionMatches: (divisionId) => {
    debugApiCall(`/league-management/admin/divisions/${divisionId}/matches`);
    return adminApi.get(`/league-management/admin/divisions/${divisionId}/matches`);
  },
  updateMatchStatus: (matchId, status, notes) => {
    debugApiCall(`/league-management/admin/matches/${matchId}/status`, { status, notes });
    return adminApi.put(`/league-management/admin/matches/${matchId}/status`, { status, notes });
  },
  updateHomeAway: (matchId, home_team_id) => {
    debugApiCall(`/league-management/admin/matches/${matchId}/home-away`, { home_team_id });
    return adminApi.put(`/league-management/admin/matches/${matchId}/home-away`, { home_team_id });
  },
  getAllMatches: (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    debugApiCall(`/league-management/admin/matches?${queryParams}`);
    return adminApi.get(`/league-management/admin/matches?${queryParams}`);
  },
  
  // Match Generation APIs
  generateDivisionMatches: (divisionId) => {
    debugApiCall(`/match-generation/admin/divisions/${divisionId}/auto-generate-matches`);
    return adminApi.post(`/match-generation/admin/divisions/${divisionId}/auto-generate-matches`);
  },
  recalculateDivisionMatches: (divisionId) => {
    debugApiCall(`/match-generation/admin/divisions/${divisionId}/recalculate-matches`);
    return adminApi.post(`/match-generation/admin/divisions/${divisionId}/recalculate-matches`);
  },
  generateAllMatches: (seasonId) => {
    debugApiCall(`/match-generation/admin/seasons/${seasonId}/generate-all-matches`);
    return adminApi.post(`/match-generation/admin/seasons/${seasonId}/generate-all-matches`);
  },
  getDivisionMatchStatus: (divisionId) => {
    debugApiCall(`/match-generation/admin/divisions/${divisionId}/match-status`);
    return adminApi.get(`/match-generation/admin/divisions/${divisionId}/match-status`);
  },
  getSeasonMatchStatus: (seasonId) => {
    debugApiCall(`/match-generation/admin/seasons/${seasonId}/match-status`);
    return adminApi.get(`/match-generation/admin/seasons/${seasonId}/match-status`);
  },
  
  // Split Groups APIs — pass seasonId so the server locks/checks/assigns for the same season as the League Management selector (not "latest assigned_at" across seasons).
  splitGroups: (divisionId, numberOfGroups, seasonId) => {
    const body = { divisionId, numberOfGroups };
    if (seasonId) body.seasonId = seasonId;
    debugApiCall(`/admin/split-groups`, body);
    // No timeout: one DB transaction; N team rows previously caused long runs; bulk upsert is fast but
    // reverse proxies can still be slow; avoid false client aborts.
    return adminApi.post(`/admin/split-groups`, body, { timeout: 0 });
  },
  getSplitGroupsStatus: (divisionId) => {
    debugApiCall(`/admin/split-groups/status/${divisionId}`);
    return adminApi.get(`/admin/split-groups/status/${divisionId}`);
  },
  unsplitGroups: (parentDivisionId, seasonId) => {
    const body = { parentDivisionId };
    if (seasonId) body.seasonId = seasonId;
    debugApiCall(`/admin/unsplit-groups`, body);
    return adminApi.post(`/admin/unsplit-groups`, body, { timeout: 0 });
  },
};

// Direct adminApi export for custom endpoints
// Teammate Finder Management APIs
export const adminTeammateFinderAPI = {
  getAllProfiles: (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    debugApiCall(`/admin/teammate-finder?${queryParams}`);
    return adminApi.get(`/admin/teammate-finder?${queryParams}`);
  },
  updateProfile: (id, profileData) => {
    debugApiCall(`/admin/teammate-finder/${id}`, profileData);
    return adminApi.put(`/admin/teammate-finder/${id}`, profileData);
  },
  deleteProfile: (id) => {
    debugApiCall(`/admin/teammate-finder/${id}`);
    return adminApi.delete(`/admin/teammate-finder/${id}`);
  },
  getFeatureSetting: () => {
    debugApiCall('/admin/teammate-finder/feature-setting');
    return adminApi.get('/admin/teammate-finder/feature-setting');
  },
  updateFeatureSetting: (enabled) => {
    debugApiCall('/admin/teammate-finder/feature-setting', { enabled });
    return adminApi.put('/admin/teammate-finder/feature-setting', { enabled });
  },
};

/** Full admin audit: HTTP actions, row-level DB changes, impersonation, CSV export */
export const adminAuditAPI = {
  listActions: (params = {}) => adminApi.get('/admin/audit/actions', { params }),
  listRows: (params = {}) => adminApi.get('/admin/audit/rows', { params }),
  listImpersonations: (params = {}) => adminApi.get('/admin/audit/impersonations', { params }),
  listAdmins: () => adminApi.get('/admin/audit/admins'),
  listTables: () => adminApi.get('/admin/audit/tables'),
  exportCsv: (params = {}) =>
    adminApi.get('/admin/audit/export', { params, responseType: 'blob' }),
};

export const adminAPI = adminApi;

export default adminApi;
